import { camelize, onMounted, unref } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { filterDeclares, forwardedPropsFor, ownProps } from "#ce/components/SpFinderFilter/props";

// HOST_ATTRIBUTES are the ones the platform defines on every element, plus `value` and `open`,
// which the element reads as attributes.
const HOST_ATTRIBUTES = Object.freeze([
  "class",
  "style",
  "id",
  "slot",
  "part",
  "hidden",
  "tabindex",
  "title",
  "lang",
  "dir",
  "role",
  "value",
  "open",
]);

const PREFIXED_ATTRIBUTE = /^(?:data|aria)-/;

function isNativeHostAttribute(host, attribute) {
  if (attribute === "disabled") {
    return host?.constructor.formAssociated === true;
  }

  let prototype = Object.getPrototypeOf(host);

  while (prototype) {
    if (Object.getOwnPropertyNames(prototype).some((name) => name.toLowerCase() === attribute.toLowerCase())) {
      return true;
    }
    prototype = Object.getPrototypeOf(prototype);
  }

  return false;
}

/**
 * Warns once, at mount and in development only, about every host attribute that reaches nothing:
 * neither the platform's, nor the element's own, nor a binding to the mounted filter.
 *
 * @param {import("vue").MaybeRef<String>} type - the mounted filter's type
 */
export function useIgnoredAttributesWarning(type) {
  if (!import.meta.env.DEV) {
    return;
  }

  const host = useHostElement();

  function isIgnored(attribute) {
    if (
      PREFIXED_ATTRIBUTE.test(attribute) ||
      HOST_ATTRIBUTES.includes(attribute) ||
      isNativeHostAttribute(host, attribute)
    ) {
      return false;
    }

    const name = camelize(attribute);

    const filterType = unref(type);
    const isBoundAttribute = name === "prependIcon" && filterDeclares(filterType, name);

    return !Object.hasOwn(ownProps, name) && !Object.hasOwn(forwardedPropsFor(filterType), name) && !isBoundAttribute;
  }

  function warnAboutIgnoredAttributes() {
    const ignored = (host?.getAttributeNames?.() ?? []).filter(isIgnored);

    if (ignored.length > 0) {
      console.warn(
        `<sp-finder-filter type="${unref(type)}"> ignores ${ignored.join(", ")}: these attributes have no consumer.`,
      );
    }
  }

  onMounted(warnAboutIgnoredAttributes);
}
